import React, { useState, useEffect } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Form, Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { SendOTP, VerifyOTP } from "../../../../features/auth/Slice";

const OTP_TIMER = 60;

function Phone_Number_Reset({ register, errors, watch, setValue }) {
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [numberLocked, setNumberLocked] = useState(false); // locks number field after send
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [countdown, setCountdown] = useState(0);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const resetOTPState = () => {
    setOtpSent(false);
    setOtpVerified(false);
    setOtpError("");
    setCountdown(0);
    setValue("otp", ""); // clear OTP field
  };

  const handleChangeNumber = () => {
    setNumberLocked(false);
    resetOTPState();
  };

  const handleSendOTP = async () => {
    const contact_no = watch("contact_no");
    if (!contact_no || contact_no.length < 10) {
      setOtpError("Please enter a valid 10-digit phone number first");
      return;
    }
    setOtpError("");
    setOtpLoading(true);
    try {
      const formData = new FormData();
      formData.append("contact_no", contact_no);
      formData.append("type", "phone");

      const res = await dispatch(SendOTP(formData));

      if (res.meta?.requestStatus === "fulfilled") {
        setNumberLocked(true); // lock number field
        setOtpSent(true);
        setOtpVerified(false);
        setCountdown(OTP_TIMER);
        setOtpError("");
      } else {
        setOtpError(res?.payload?.errors || "Failed to send OTP. Try again.");
      }
    } catch (err) {
      setOtpError("Something went wrong.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    const otp = watch("otp");
    const contact_no = watch("contact_no");
    if (!otp || otp.length < 4) {
      setOtpError("Please enter the OTP");
      return;
    }
    setOtpError("");
    setVerifyLoading(true);
    try {
      const formData = new FormData();
      formData.append("contact_no", contact_no);
      formData.append("otp", otp);
      formData.append("type", "phone");

      const res = await dispatch(VerifyOTP(formData));

      if (res.meta?.requestStatus === "fulfilled") {
        setOtpVerified(true);
        setOtpError("");
      } else {
        setOtpError(res?.payload?.errors || "Invalid OTP. Try again.");
      }
    } catch (err) {
      setOtpError("Something went wrong.");
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <>
      {/* Phone Number */}
      <div className="form-group field_holder">
        <Form.Label className="d-flex justify-content-between align-items-center">
          <span>
            <img src="/assets/images/icons/phone.png" alt="icon" className="login_lable_icons" />{" "}
            Phone number
          </span>
          {/* Change Number link — only shown when number is locked */}
          {numberLocked && !otpVerified && (
            <small
              className="text"
              style={{ cursor: "pointer", fontSize: "12px" }}
              onClick={handleChangeNumber}
            >
              Change Number
            </small>
          )}
          {otpVerified && (
            <small
              className="text"
              style={{ cursor: "pointer", fontSize: "12px" }}
              onClick={handleChangeNumber}
            >
              Change Number
            </small>
          )}
        </Form.Label>

        <Form.Control
          placeholder="Enter 10-digit phone number"
          type="text"
          maxLength={10}
          disabled={numberLocked} // locked after OTP sent
          {...register("contact_no", {
            required: "Phone number is required",
            pattern: {
              value: /^[0-9]+$/,
              message: "Only digits are allowed",
            },
            minLength: {
              value: 10,
              message: "Phone number must be exactly 10 digits",
            },
            maxLength: {
              value: 10,
              message: "Phone number must be exactly 10 digits",
            },
          })}
        />
        {errors.contact_no && (
          <small className="text-danger">{errors.contact_no.message}</small>
        )}
      </div>

      {/* Set Password */}
      <div className="form-group field_holder">
        <Form.Label>
          <img src="/assets/images/icons/lock.png" alt="icon" className="login_lable_icons" />{" "}
          Set Password
        </Form.Label>
        <div className="password_input">
          <Form.Control
            type={showPassword ? "text" : "password"}
            placeholder="Set Password"
            {...register("password", {
              required: "Password is required",
              minLength: { value: 6, message: "Password must be at least 6 characters" },
            })}
          />
          <span className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
            {showPassword ? <FaEye /> : <FaEyeSlash />}
          </span>
        </div>
        {errors.password && (
          <small className="text-danger">{errors.password.message}</small>
        )}
      </div>

      {/* Confirm Password */}
      <div className="form-group field_holder">
        <Form.Label>
          <img src="/assets/images/icons/lock.png" alt="icon" className="login_lable_icons" />{" "}
          Confirm Password
        </Form.Label>
        <div className="password_input">
          <Form.Control
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm Password"
            {...register("confirm_password", {
              required: "Please confirm your password",
              validate: (value) => value === watch("password") || "Passwords do not match",
            })}
          />
          <span className="eye-icon" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
            {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
          </span>
        </div>
        {errors.confirm_password && (
          <small className="text-danger">{errors.confirm_password.message}</small>
        )}
      </div>

      {/* OTP */}
      <div className="form-group field_holder invite_code_wrapper">
        <Form.Label>
          <img src="/assets/images/icons/sheild.png" alt="icon" className="login_lable_icons" />{" "}
          OTP
          {otpVerified && <small className="text-success ms-2">✔ Verified</small>}
        </Form.Label>

        <div className="invite_input">
          <Form.Control
            type="text"
            placeholder="Please Enter the OTP"
            disabled={otpVerified} // lock OTP field after verified
            {...register("otp", {
              required: "OTP is required",
              minLength: { value: 4, message: "Enter a valid OTP" },
            })}
          />


          {!otpVerified && (
            !otpSent || countdown === 0 ? (
              <Button
                className="send_btn"
                type="button"
                onClick={handleSendOTP}
                disabled={otpLoading}
              >
                {otpLoading ? "Sending..." : otpSent ? "Resend OTP" : "Send OTP"}
              </Button>
            ) : (
              <Button
                className="send_btn"
                type="button"
                onClick={handleVerifyOTP}
                disabled={verifyLoading}
              >
                {verifyLoading ? "Verifying..." : `Verify OTP (${countdown}s)`}
              </Button>
            )
          )}
        </div>

        {otpError && <small className="text-danger d-block mt-1">{otpError}</small>}
        {errors.otp && <small className="text-danger d-block">{errors.otp.message}</small>}
      </div>
    </>
  );
}

export default Phone_Number_Reset;